import { useState, useEffect } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from '@heroicons/react/24/solid'
import { fetchContent, urlFor, subscribeToType, LIVE_MODE } from '../lib/sanity'

interface Transformation {
  name: string
  weightLoss: string
  beforeImage: {
    asset: {
      url: string
    }
  }
  afterImage: {
    asset: {
      url: string
    }
  }
}

interface BeforeAfterContent {
  heading: string
  transformations: Transformation[]
}

interface BeforeAfterProps {
  data?: BeforeAfterContent | null
}

const fallbackTransformations: Transformation[] = [
  {
    name: "Andrea's approach",
    weightLoss: 'A focus on routines, confidence, and sustainable lifestyle change',
    beforeImage: { asset: { url: '/images/person-andrea-smiling.png' } },
    afterImage: { asset: { url: '/images/person-andrea-smiling.png' } },
  },
]

function getImageUrl(image: Transformation['beforeImage']): string {
  const imageUrl = image?.asset?.url
  return imageUrl?.startsWith('/') ? imageUrl : urlFor(image).url()
}

const sliderVariants = {
  enter: (direction: number) => ({ x: direction > 0 ? 160 : -160, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction < 0 ? 160 : -160, opacity: 0 }),
}

const BeforeAfter = ({ data }: BeforeAfterProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovering, setIsHovering] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [content, setContent] = useState<BeforeAfterContent | null>(null)
  const [direction, setDirection] = useState(0)
  const prefersReducedMotion = useReducedMotion() ?? false

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    if (data) {
      setContent(data)
    } else {
      const getContent = async () => {
        try {
          const [fetchedData] = await fetchContent('beforeAfter')
          setContent(fetchedData)
          if (LIVE_MODE) {
            unsubscribe = subscribeToType('beforeAfter', (docs) => setContent(docs[0]))
          }
        } catch (error) {
          console.error('Error fetching before/after content:', error)
        }
      }
      getContent()
    }
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [data])

  const transformations = content?.transformations || fallbackTransformations
  const totalPairs = transformations.length

  useEffect(() => {
    if (totalPairs === 0 || isHovering || isFocused || isPaused || prefersReducedMotion) return
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % totalPairs
        setDirection(1)
        return next
      })
    }, 5000)
    return () => clearInterval(intervalId)
  }, [totalPairs, isHovering, isFocused, isPaused, prefersReducedMotion])

  const goToPrevious = () => {
    setCurrentIndex((prev) => {
      const next = prev === 0 ? totalPairs - 1 : prev - 1
      setDirection(-1)
      return next
    })
  }

  const goToNext = () => {
    setCurrentIndex((prev) => {
      const next = (prev + 1) % totalPairs
      setDirection(1)
      return next
    })
  }

  const currentTransformation = transformations[currentIndex]
  const variants = prefersReducedMotion
    ? {
        enter: { opacity: 0 },
        center: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : sliderVariants
  const toggleAutoPlay = () => {
    setIsPaused((prev) => !prev)
    setIsFocused(false)
    setIsHovering(false)
  }

  if (!currentTransformation) {
    return null
  }

  return (
    <section className="relative isolate overflow-hidden bg-[#cdfbf1] py-24">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="container relative z-10 mx-auto px-6">
        <div className="grid items-center gap-16 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <div
              className="rounded-[32px] border border-white/80 bg-white p-8 shadow-[0_25px_60px_rgba(6,96,94,0.12)]"
              role="region"
              aria-roledescription="carousel"
              aria-label="Before and after stories"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              onFocusCapture={() => setIsFocused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsFocused(false)
              }}
            >
              <p className="text-xs uppercase tracking-[0.5em] text-teal-700">Before & After</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-slate-900">
                {content?.heading || 'Real stories. Real routines. Real people.'}
              </h2>

              <div className="mt-10">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={currentIndex}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: prefersReducedMotion ? 0 : 0.45, ease: 'easeOut' }}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`Transformation ${currentIndex + 1} of ${totalPairs}`}
                    className="grid gap-6 md:grid-cols-2"
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm uppercase tracking-[0.4em] text-slate-500">Before</p>
                      </div>
                      <div className="flex h-80 w-full items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 md:h-96">
                        <img
                          src={getImageUrl(currentTransformation.beforeImage)}
                          alt={`Before photo of ${currentTransformation.name}`}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm uppercase tracking-[0.4em] text-slate-500">After</p>
                      </div>
                      <div className="flex h-80 w-full items-center justify-center overflow-hidden rounded-2xl border border-teal-200 bg-white md:h-96">
                        <img
                          src={getImageUrl(currentTransformation.afterImage)}
                          alt={`After photo of ${currentTransformation.name}`}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <p className="sr-only" aria-live="polite" aria-atomic="true">
                Transformation {currentIndex + 1} of {totalPairs}: {currentTransformation.name}. {currentTransformation.weightLoss}
              </p>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-lg font-semibold text-slate-900">{currentTransformation.name}</p>
                  <p className="text-sm text-slate-500">{currentTransformation.weightLoss}</p>
                </div>
                <div className="flex items-center justify-end gap-4">
                  <button
                    type="button"
                    onClick={goToPrevious}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-teal-200 text-teal-700 transition hover:bg-[#cdfbf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                    aria-label="Previous transformation"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  {!prefersReducedMotion && (
                    <button
                      type="button"
                      onClick={toggleAutoPlay}
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-teal-200 text-teal-700 transition hover:bg-[#cdfbf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                      aria-label={isPaused ? 'Resume automatic transformations' : 'Pause automatic transformations'}
                      aria-pressed={isPaused}
                    >
                      {isPaused ? <PlayIcon className="h-5 w-5" /> : <PauseIcon className="h-5 w-5" />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={goToNext}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-teal-200 text-teal-700 transition hover:bg-[#cdfbf1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2"
                    aria-label="Next transformation"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6 text-slate-700">
            <p className="text-xs uppercase tracking-[0.5em] text-teal-600">Intentional layering</p>
            <h3 className="text-3xl font-semibold text-slate-900">A visual record of personal change</h3>
            <p className="text-base leading-relaxed">
              We keep every photo supplied directly by members to honor privacy and reality. Nothing staged, nothing filtered. Andrea's role is non-medical accountability and lifestyle support alongside the licensed care each person chooses.
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-teal-500" />Routine and habit support that fits real life</li>
              <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-teal-500" />Accountability check-ins for consistency</li>
              <li className="flex items-center gap-3"><span className="h-2 w-2 rounded-full bg-teal-500" />Clinical questions redirected to your licensed care team</li>
            </ul>
            <div className="rounded-3xl border border-teal-200/70 bg-white p-6 shadow-sm">
              <p className="font-semibold text-slate-900">Personal data stays yours.</p>
              <p className="text-sm text-slate-600">Need your photos removed or updated? Ping Andrea directly and we will rotate the carousel within the same day.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BeforeAfter
